import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import CodeMark from '@tiptap/extension-code';
import { Bold, Italic, List, ListOrdered, Link2, Heading2, Undo2, Redo2, Superscript as SuperscriptIcon, Subscript as SubscriptIcon } from 'lucide-react';

// The stock Code mark excludes ALL other marks (`excludes: '_'`) — but
// LeetCode-imported constraints wrap exponents in `<code>...<sup>9</sup></code>`
// (e.g. `-10⁹ <= target <= 10⁹`), so importing that HTML unmodified would
// silently flatten the superscript to plain text. Only exclude the marks
// that genuinely shouldn't mix with inline code.
const Code = CodeMark.extend({ excludes: 'bold italic strike' });

interface Props {
  value?: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function ToolbarButton({ active, onClick, label, children }: {
  active?: boolean; onClick: () => void; label: string; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex size-7 items-center justify-center rounded-md transition-colors ${
        active ? 'bg-[#ede9fe] text-[#8b5cf6]' : 'text-[#6b7280] hover:bg-[#f3f4f6]'
      }`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const promptLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[#ececec] px-1.5 py-1">
      <ToolbarButton label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="size-3.5" /></ToolbarButton>
      <span className="mx-1 h-4 w-px bg-[#ececec]" />
      <ToolbarButton label="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Link" active={editor.isActive('link')} onClick={promptLink}><Link2 className="size-3.5" /></ToolbarButton>
      <span className="mx-1 h-4 w-px bg-[#ececec]" />
      <ToolbarButton label="Superscript" active={editor.isActive('superscript')} onClick={() => editor.chain().focus().toggleSuperscript().run()}><SuperscriptIcon className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Subscript" active={editor.isActive('subscript')} onClick={() => editor.chain().focus().toggleSubscript().run()}><SubscriptIcon className="size-3.5" /></ToolbarButton>
      <span className="mx-1 h-4 w-px bg-[#ececec]" />
      <ToolbarButton label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="size-3.5" /></ToolbarButton>
      <ToolbarButton label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="size-3.5" /></ToolbarButton>
    </div>
  );
}

/** Tiptap-based rich text editor; emits sanitized-on-render HTML via onChange. */
export function RichTextEditor({ value, onChange, placeholder }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, code: false }),
      Code,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Superscript,
      Subscript,
    ],
    content: value ?? '',
    editorProps: { attributes: { class: 'px-3 py-2.5 text-sm text-[#374151]' } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    immediatelyRender: false,
  });

  if (!editor) return null;

  return (
    <div className="kp-rich overflow-hidden rounded-md border border-border bg-background focus-within:ring-2 focus-within:ring-ring">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
